import { useState } from 'react';
import { apiClient } from '@/api/client';
import { ATTACHMENT_UPLOAD_TIMEOUT_MS } from '@/api/constants';
import { API_ATTACHMENTS } from '@/api/endpoints';
import {
  AttachmentErrorKind,
  AttachmentUploadOrigin,
  AttachmentUploadStatus,
} from '@/types/components';
import type {
  Attachment,
  AttachmentUploadResponse,
  PotentialAttachment,
} from '@/types/attachment';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';

export const MAX_ATTACHMENT_SIZE_MB = 30;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

let attachmentKeyCounter = 0;

const uploadFile = async (file: File, origin: AttachmentUploadOrigin) => {
  const formData = new FormData();

  formData.append('uploadOrigin', origin);
  formData.append('files', file);

  const response = await apiClient.post<AttachmentUploadResponse[]>(
    API_ATTACHMENTS,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: ATTACHMENT_UPLOAD_TIMEOUT_MS,
    },
  );
  return unwrapAxiosResponse(response);
};

interface Params {
  origin: AttachmentUploadOrigin;
  initialAttachments?: Attachment[];
}

export const useAttachmentUploads = ({
  origin,
  initialAttachments = [],
}: Params) => {
  const [attachments, setAttachments] = useState<PotentialAttachment[]>(() =>
    initialAttachments.map((attachment) => ({
      key: attachment.id,
      id: attachment.id,
      name: attachment.name,
      sizeInBytes: attachment.sizeInBytes,
      isUploading: false,
    })),
  );

  const updateAttachment = (
    key: string,
    changes: Partial<PotentialAttachment>,
  ) => {
    setAttachments((prev) =>
      prev.map((attachment) =>
        attachment.key === key ? { ...attachment, ...changes } : attachment,
      ),
    );
  };

  const addFiles = async (files: File[]) => {
    const uploads = files.map(async (file) => {
      attachmentKeyCounter += 1;
      const key = `attachment-${attachmentKeyCounter}`;
      const isOversize = file.size > MAX_ATTACHMENT_SIZE_BYTES;

      setAttachments((prev) => [
        ...prev,
        {
          key,
          name: file.name,
          sizeInBytes: file.size,
          isUploading: !isOversize,
          errorKind: isOversize ? AttachmentErrorKind.Oversize : undefined,
        },
      ]);

      if (isOversize) {
        return;
      }

      try {
        const [uploaded] = await uploadFile(file, origin);

        if (uploaded?.status !== AttachmentUploadStatus.Ok) {
          updateAttachment(key, {
            isUploading: false,
            errorKind:
              uploaded?.status === AttachmentUploadStatus.InvalidFileType
                ? AttachmentErrorKind.InvalidType
                : AttachmentErrorKind.UploadFailed,
          });
          return;
        }

        updateAttachment(key, { id: uploaded.id, isUploading: false });
      } catch (error) {
        logRequestError(ErrorMessage.UploadAttachment, error);
        updateAttachment(key, {
          isUploading: false,
          errorKind: AttachmentErrorKind.UploadFailed,
        });
      }
    });

    await Promise.all(uploads);
  };

  const clearAttachments = () => setAttachments([]);

  const removeAttachment = (key: string) => {
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.key !== key),
    );
  };

  const isReadyForSending = attachments.every(
    (attachment) => attachment.id && !attachment.errorKind,
  );
  const uploadedAttachments = attachments.flatMap((attachment) =>
    attachment.id && !attachment.errorKind
      ? [
          {
            id: attachment.id,
            name: attachment.name,
            sizeInBytes: attachment.sizeInBytes,
          },
        ]
      : [],
  );

  return {
    attachments,
    uploadedAttachments,
    isReadyForSending,
    addFiles,
    removeAttachment,
    clearAttachments,
  };
};
